import { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, IconButton, Text } from '@radix-ui/themes';
import { Check, ClipboardCopy, RefreshCw, X } from 'lucide-react';
import { copyText } from '../lib/clipboard';
import { getTelegramWebApp } from '../lib/telegram';

interface Props {
  text: string;
  onClose: () => void;
  onRefresh: () => void;
}

/**
 * The session's output as text you can actually take with you.
 *
 * The terminal itself can never offer this. xterm ships `user-select: none`, and the
 * host swallows every touch to translate it into the scroll reports a TUI needs — so a
 * long press there has nothing to select and nothing to show. Rather than fight that,
 * this is a snapshot rendered as ordinary text, where the platform's own selection
 * handles work exactly as they do anywhere else.
 *
 * A snapshot, deliberately: Codex repaints constantly, and text shifting under a finger
 * mid-selection is worse than text that is thirty seconds old. Refresh is a button.
 */
export function TermText({ text, onClose, onRefresh }: Props) {
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);

  // The newest lines are the ones worth copying, so open at the bottom — and land
  // there again after a refresh, which is only ever asked for to see what just arrived.
  useEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [text]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    const wa = getTelegramWebApp();
    if (await copyText(text)) {
      setCopied(true);
      wa?.HapticFeedback?.notificationOccurred('success');
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    } else {
      wa?.showAlert?.('Не получилось скопировать целиком. Выдели нужный кусок пальцем — обычное выделение здесь работает.');
    }
  };

  return (
    <Flex
      direction="column"
      className="text-sheet"
      style={{ position: 'fixed', inset: 0, zIndex: 45, background: 'var(--app-bg)' }}
    >
      <Flex align="center" justify="between" px="3" py="2" gap="2" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
        <Text size="2" weight="bold" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Текст сессии
        </Text>
        <Flex align="center" gap="2" style={{ flex: '0 0 auto' }}>
          <Button size="1" variant="soft" color={copied ? 'jade' : 'gray'} disabled={!text} onClick={copy}>
            {copied ? <Check size={13} /> : <ClipboardCopy size={13} />}
            {copied ? 'Скопировано' : 'Скопировать всё'}
          </Button>
          <IconButton size="1" variant="ghost" color="gray" title="Обновить снимок" onClick={onRefresh}>
            <RefreshCw size={15} />
          </IconButton>
          <IconButton size="1" variant="ghost" color="gray" title="Закрыть" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </Flex>
      </Flex>

      <div ref={bodyRef} className="text-sheet__body">
        {text ? (
          <pre className="text-sheet__text">{text}</pre>
        ) : (
          <Flex align="center" justify="center" style={{ height: '100%', padding: 24 }}>
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              В сессии пока пусто
            </Text>
          </Flex>
        )}
      </div>

      <Box px="3" py="2" style={{ borderTop: '1px solid var(--gray-a4)' }}>
        <Text size="1" style={{ color: 'var(--app-hint)' }}>
          Долгий тап по тексту — выделить кусок. Снимок на момент открытия.
        </Text>
      </Box>
    </Flex>
  );
}
